import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Send, Clock } from 'lucide-react';
import { useTranslation } from '../i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useAnalytics } from '../hooks/useAnalytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = ({ lang }) => {
  const { t } = useTranslation(lang);
  useAnalytics('contact');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [reviewForm, setReviewForm] = useState({
    customer_name: '',
    customer_email: '',
    rating: 5,
    comment: '',
    review_type: 'general'
  });
  const [submitting, setSubmitting] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t('contact.success'));
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error(t('contact.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);
    
    try {
      // Use unified reviews endpoint
      await axios.post(`${API}/reviews`, reviewForm);
      toast.success(t('reviews.generalReviewSubmitted'));
      setReviewForm({ customer_name: '', customer_email: '', rating: 5, comment: '', review_type: 'general' });
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(t('reviews.reviewFailed'));
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <>
      <SEOHead 
        title="Contact Us | AAA of Miami | Get in Touch"
        description="Contact AAA of Miami for inquiries about our premium vehicles. Located in Miami, FL. Call us or send a message for test drives, pricing, and vehicle availability."
        type="website"
      />
      <div className="pt-20 min-h-screen" style={{ background: 'var(--bg-secondary)' }}>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }} data-testid="contact-title">
            {t('contact.title')}
          </h1>
          <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>{t('common.getTouchTeam')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="rounded-lg p-8 shadow-lg" style={{ background: 'var(--bg-primary)' }}>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>{t('common.sendMessage')}</h2>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('contact.name')}</label>
                <Input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  required
                  style={{
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)'
                  }}
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('contact.email')}</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({...form, email: e.target.value})}
                  required
                  style={{
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)'
                  }}
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('contact.phone')}</label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({...form, phone: e.target.value})}
                  style={{
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)'
                  }}
                  data-testid="contact-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('contact.subject')}</label>
                <Input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({...form, subject: e.target.value})}
                  required
                  style={{
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)'
                  }}
                  data-testid="contact-subject"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('contact.message')}</label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({...form, message: e.target.value})}
                  required
                  rows={6}
                  style={{
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)'
                  }}
                  data-testid="contact-message"
                />
              </div>
              <Button type="submit" className="w-full bg-gray-900 hover:bg-gray-800 text-white" disabled={submitting} data-testid="contact-submit">
                {submitting ? t('contactPage.sending') : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t('contact.send')}
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div className="rounded-lg p-8 shadow-lg" style={{ background: 'var(--bg-primary)' }}>
              <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>{t('common.visitUs')}</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4" data-testid="contact-address">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--bg-tertiary)' }}>
                    <MapPin className="w-6 h-6" style={{ color: 'var(--text-primary)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{t('common.address')}</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>AAA OF MIAMI LLC</p>
                    <p style={{ color: 'var(--text-secondary)' }}>8125 NW 56th St</p>
                    <p style={{ color: 'var(--text-secondary)' }}>Doral, FL 33166</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-testid="contact-phone-info">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--bg-tertiary)' }}>
                    <Phone className="w-6 h-6" style={{ color: 'var(--text-primary)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{t('contact.phone')}</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>+1 (305) 457-8831</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-testid="contact-email-info">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--bg-tertiary)' }}>
                    <Mail className="w-6 h-6" style={{ color: 'var(--text-primary)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{t('contact.email')}</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>aaaofmiami@gmail.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp Contact Card */}
            <div className="rounded-lg p-8 shadow-lg bg-gradient-to-br from-green-500 to-green-600 text-white">
              <div className="flex items-center space-x-3 mb-4">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <h3 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display' }}>{t('contact.whatsapp')}</h3>
              </div>
              <p className="text-green-50 mb-4">{t('contact.whatsappDesc')}</p>
              <Button
                onClick={() => {
                  const message = t('contact.whatsappDefaultMessage');
                  const whatsappUrl = `https://wa.me/13054578831?text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, '_blank');
                }}
                className="w-full bg-white text-green-600 hover:bg-green-50 font-semibold"
                data-testid="contact-whatsapp-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                {t('contact.chatWhatsapp')}
              </Button>
            </div>

            <div className="rounded-lg p-8 shadow-lg bg-gradient-to-br from-gray-900 to-gray-700 text-white">
              <div className="flex items-center space-x-3 mb-4">
                <Clock className="w-6 h-6" />
                <h3 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display' }}>{t('common.businessHours')}</h3>
              </div>
              <div className="space-y-2 text-gray-200">
                <div className="flex justify-between">
                  <span>{t('contactPage.mondayFriday')}</span>
                  <span className="font-semibold">{t('contactPage.mondayFridayHours')}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('contactPage.saturday')}</span>
                  <span className="font-semibold">{t('contactPage.saturdayHours')}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('contactPage.sunday')}</span>
                  <span className="font-semibold">{t('contactPage.sundayHours')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* General Review Section */}
        <div className="mt-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
              {t('reviews.writeGeneralReview')}
            </h2>
            <p className="text-xl" style={{ color: 'var(--text-secondary)' }}>
              {t('reviews.aboutOurService')}
            </p>
          </div>

          <div className="max-w-3xl mx-auto rounded-lg p-8 shadow-lg" style={{ background: 'var(--bg-primary)' }}>
            <form onSubmit={handleReviewSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Input
                    placeholder={t('reviews.yourName')}
                    value={reviewForm.customer_name}
                    onChange={(e) => setReviewForm({...reviewForm, customer_name: e.target.value})}
                    required
                    style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder={t('reviews.yourEmail')}
                    value={reviewForm.customer_email}
                    onChange={(e) => setReviewForm({...reviewForm, customer_email: e.target.value})}
                    required
                    style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  />
                </div>
              </div>

              <div>
                <label className="block mb-3 font-medium text-lg" style={{ color: 'var(--text-primary)' }}>
                  {t('reviews.reviewType')}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setReviewForm({...reviewForm, review_type: 'general'})}
                    className="p-4 rounded-lg border-2 transition-all"
                    style={{
                      background: reviewForm.review_type === 'general' ? 'var(--accent-gold)' : 'var(--bg-secondary)',
                      borderColor: reviewForm.review_type === 'general' ? 'var(--accent-gold)' : 'var(--border-color)',
                      color: reviewForm.review_type === 'general' ? '#000' : 'var(--text-primary)'
                    }}
                  >
                    {t('reviews.generalReview')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setReviewForm({...reviewForm, review_type: 'service'})}
                    className="p-4 rounded-lg border-2 transition-all"
                    style={{
                      background: reviewForm.review_type === 'service' ? 'var(--accent-gold)' : 'var(--bg-secondary)',
                      borderColor: reviewForm.review_type === 'service' ? 'var(--accent-gold)' : 'var(--border-color)',
                      color: reviewForm.review_type === 'service' ? '#000' : 'var(--text-primary)'
                    }}
                  >
                    {t('reviews.serviceReview')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setReviewForm({...reviewForm, review_type: 'dealership'})}
                    className="p-4 rounded-lg border-2 transition-all"
                    style={{
                      background: reviewForm.review_type === 'dealership' ? 'var(--accent-gold)' : 'var(--bg-secondary)',
                      borderColor: reviewForm.review_type === 'dealership' ? 'var(--accent-gold)' : 'var(--border-color)',
                      color: reviewForm.review_type === 'dealership' ? '#000' : 'var(--text-primary)'
                    }}
                  >
                    {t('reviews.dealershipReview')}
                  </button>
                </div>
              </div>

              <div>
                <label className="block mb-3 font-medium text-lg" style={{ color: 'var(--text-primary)' }}>
                  {t('reviews.yourRating')}
                </label>
                <div className="flex space-x-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewForm({...reviewForm, rating: star})}
                      className="text-5xl focus:outline-none transition-all hover:scale-110"
                      style={{ color: star <= reviewForm.rating ? '#fbbf24' : '#9ca3af' }}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Textarea
                  placeholder={t('reviews.yourReview')}
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                  required
                  rows={6}
                  style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                />
              </div>

              <Button
                type="submit"
                disabled={submittingReview}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
              >
                {submittingReview ? t('contactPage.sending') : t('reviews.submitReview')}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ContactPage;
